<template>
	<teleport to="body">
		<div
			v-if="isVisible && hasContent && !hideTooltip"
			ref="tooltipEl"
			class="nk-button-tooltip"
			:class="{ 'is-active': isPopperReady }"
			role="tooltip"
		>
			<div class="nk-button-tooltip-content">
				<span v-if="shouldShowTitle" class="nk-tooltip-title">{{ title }}</span>
				<div v-if="shouldShowShortcut" class="nk-tooltip-shortcuts" dir="ltr">
					<kbd v-for="(key, index) in shortcutKeys" :key="index" class="nk-tooltip-key">{{ key }}</kbd>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script>
	import { createPopper } from '@popperjs/core'

	const KEY_CODE_MAP = {
		8: 'Backspace',
		9: 'Tab',
		13: 'Enter',
		27: 'Esc',
		32: 'Space',
		33: 'PageUp',
		34: 'PageDown',
		35: 'End',
		36: 'Home',
		37: 'Left',
		38: 'Up',
		39: 'Right',
		40: 'Down',
		45: 'Insert',
		46: 'Delete',
		112: 'F1',
		113: 'F2',
		114: 'F3',
		115: 'F4',
		116: 'F5',
		117: 'F6',
		118: 'F7',
		119: 'F8',
		120: 'F9',
		121: 'F10',
		122: 'F11',
		123: 'F12',
	}

	export default {
		name: 'NkTooltip',
		props: {
			target: {
				type: [Object, Function, String],
				default: null,
			},
			title: {
				type: String,
				default: '',
			},
			shortcut: {
				type: [Object, String, Array, Number],
				default: null,
			},
			hideTitle: {
				type: Boolean,
				default: false,
			},
			hideTooltip: {
				type: Boolean,
				default: true,
			},
			placement: {
				type: String,
				default: 'bottom',
			},
			offset: {
				type: Array,
				default: () => [0, 6],
			},
			disabled: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				isVisible: false,
				isPopperReady: false,
				popperInstance: null,
				targetEl: null,
				hideTimeout: null,
			}
		},
		computed: {
			hasTitle() {
				return !!(this.title && this.title.trim() !== '')
			},
			shortcutKeys() {
				if (!this.shortcut) return []

				if (Array.isArray(this.shortcut)) {
					return this.shortcut.map(k => String(k).toUpperCase())
				}

				if (typeof this.shortcut === 'string' && this.shortcut.trim()) {
					return this.shortcut
						.split('+')
						.map(k => k.trim().toUpperCase())
						.filter(Boolean)
				}

				const sc = typeof this.shortcut === 'object' ? this.shortcut : { keyCode: Number(this.shortcut) || 0 }
				const code = sc.keyCode || 0
				const isCtrl = !!sc.ctrlKey
				const isShift = !!sc.shiftKey
				const isAlt = !!sc.altKey

				if (code > 0 || isCtrl || isShift || isAlt) {
					const keys = []
					if (isCtrl) keys.push('CTRL')
					if (isShift) keys.push('SHIFT')
					if (isAlt) keys.push('ALT')

					if (code > 0) {
						let char = ''
						if (typeof String.fromKeyCode === 'function') {
							char = String.fromKeyCode(code)
						}
						if (!char) {
							char = KEY_CODE_MAP[code] || String.fromCharCode(code) || ''
						}
						if (char) keys.push(char.toUpperCase())
					}
					return keys
				}

				if (sc.display) {
					return sc.display
						.split('+')
						.map(k => k.trim().toUpperCase())
						.filter(Boolean)
				}

				return []
			},
			hasShortcut() {
				return this.shortcutKeys.length > 0
			},
			shouldShowTitle() {
				return this.hideTitle && this.hasTitle
			},
			shouldShowShortcut() {
				return this.hasShortcut
			},
			hasContent() {
				return this.shouldShowTitle || this.shouldShowShortcut
			},
		},
		watch: {
			target() {
				this.initTarget()
			},
		},
		mounted() {
			this.$nextTick(() => {
				this.initTarget()
			})
		},
		beforeUnmount() {
			this.cleanup()
		},
		methods: {
			resolveTarget() {
				if (this.target) {
					if (typeof this.target === 'function') {
						return this.target()
					}
					if (typeof this.target === 'string') {
						return document.querySelector(this.target)
					}
					return this.target
				}
				return this.$parent?.$el || this.$el?.parentElement
			},
			bindTargetEvents() {
				if (!this.targetEl) return
				this.onMouseEnter = this.show.bind(this)
				this.onMouseLeave = this.hide.bind(this)
				this.onFocusIn = this.show.bind(this)
				this.onFocusOut = this.hide.bind(this)
				this.onClick = this.hide.bind(this)

				this.targetEl.addEventListener('mouseenter', this.onMouseEnter)
				this.targetEl.addEventListener('mouseleave', this.onMouseLeave)
				this.targetEl.addEventListener('focusin', this.onFocusIn)
				this.targetEl.addEventListener('focusout', this.onFocusOut)
				this.targetEl.addEventListener('click', this.onClick)
			},
			initTarget() {
				this.cleanupListeners()
				this.targetEl = this.resolveTarget()
				if (!this.targetEl) {
					setTimeout(() => {
						this.targetEl = this.resolveTarget()
						if (this.targetEl) {
							this.bindTargetEvents()
						}
					}, 50)
					return
				}
				this.bindTargetEvents()
			},
			show() {
				if (this.hideTooltip || !this.hasContent || this.disabled) return

				if (this.hideTimeout) {
					clearTimeout(this.hideTimeout)
					this.hideTimeout = null
				}

				this.isVisible = true
				this.$nextTick(() => {
					this.createOrUpdatePopper()
				})
			},
			hide() {
				this.isPopperReady = false
				this.hideTimeout = setTimeout(() => {
					this.isVisible = false
					this.destroyPopper()
				}, 120)
			},
			createOrUpdatePopper() {
				const tooltipEl = this.$refs.tooltipEl
				const target = this.targetEl || this.resolveTarget()

				if (!tooltipEl || !target) return

				if (this.popperInstance) {
					this.popperInstance.update().then(() => {
						this.isPopperReady = true
					})
					return
				}

				this.popperInstance = createPopper(target, tooltipEl, {
					placement: this.placement,
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: this.offset,
							},
						},
						{
							name: 'preventOverflow',
							options: {
								padding: 8,
							},
						},
					],
					onFirstUpdate: () => {
						this.isPopperReady = true
					},
				})
			},
			destroyPopper() {
				if (this.popperInstance) {
					this.popperInstance.destroy()
					this.popperInstance = null
				}
			},
			cleanupListeners() {
				if (this.targetEl) {
					this.targetEl.removeEventListener('mouseenter', this.onMouseEnter)
					this.targetEl.removeEventListener('mouseleave', this.onMouseLeave)
					this.targetEl.removeEventListener('focusin', this.onFocusIn)
					this.targetEl.removeEventListener('focusout', this.onFocusOut)
					this.targetEl.removeEventListener('click', this.onClick)
				}
			},
			cleanup() {
				if (this.hideTimeout) {
					clearTimeout(this.hideTimeout)
					this.hideTimeout = null
				}
				this.cleanupListeners()
				this.destroyPopper()
			},
		},
	}
</script>

<style lang="less">
	.nk-button-tooltip {
		position: absolute;
		z-index: 10000;
		pointer-events: none;
		visibility: hidden;
		opacity: 0;
		transition: opacity 0.12s ease-out, visibility 0.12s ease-out;

		&.is-active {
			visibility: visible;
			opacity: 1;
		}

		.nk-button-tooltip-content {
			background-color: #ffffff;
			border: 1px solid #CBD5E1;
			border-radius: 8px;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
			padding: 4px 8px;
			display: inline-flex;
			align-items: center;
			gap: 8px;
			direction: rtl;
			white-space: nowrap;
			transform: translateY(-3px) scale(0.98);
			transition: transform 0.12s ease-out;

			.is-active & {
				transform: translateY(0) scale(1);
			}
		}

		.nk-tooltip-title {
			font-size: 13px;
			font-weight: 600;
			color: #0F172A;
			line-height: 1.3;
			font-family: inherit;
			white-space: nowrap;
			user-select: none;
		}

		.nk-tooltip-shortcuts {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			direction: ltr;

			.nk-tooltip-key {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: 18px;
				height: 18px;
				padding: 0 5px;
				font-size: 11px;
				font-weight: 600;
				font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
				color: #0F172A;
				background-color: #ffffff;
				border: 1px solid #CBD5E1;
				border-radius: 4px;
				line-height: 1;
				text-transform: uppercase;
				box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
				user-select: none;
				margin: 0;
			}
		}
	}
</style>
